import { observer } from 'mobx-react-lite';
import * as React from 'react';
import loc, { format } from 'owa-localize';
import { getPaletteAsRawColors, DEFAULT_OUTLOOK_THEME_COLOR } from 'owa-theme';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { renderAsAllDay } from 'owa-calendar-types';
import type SchedulingCalendarEvent from '../schema/SchedulingCalendarEvent';
import getCalendarOwnerFromItem from '../utils/getCalendarOwnerFromItem';
import shouldShowFreeBusy, { getEndTimeToShowFreeBusy } from '../utils/shouldShowFreeBusy';
import getConflictCount from '../utils/getConflictCount';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { freeBusyCache, FreeBusyType, isConflictFromFreeBusy } from 'owa-freebusy-cache';
import { isOriginalTime } from 'owa-calendar-event-capabilities';
import getSchedulingItemSecondLineText from '../utils/getSchedulingItemSecondLineText';
import { getIsTouchOnlyDevice } from 'owa-layout';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import { TooltipHost, TooltipOverflowMode } from '@fluentui/react/lib/Tooltip';
import { differenceInMinutes, OwaDate } from 'owa-datetime';
import getDurationText from '../utils/getDurationText';
import { schedulingAssistantAriaDateAndStatus } from 'owa-locstrings/lib/strings/schedulingassistantariadateandstatus.locstring.json';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import type { GridBookendEvents } from '../schema/GridBookendEvents';
import { isFeatureEnabled } from 'owa-feature-flags';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';

const styles = require('./SchedulingTemplates.scss');
const classNames = require('classnames/bind').bind(styles);

// Background color for selection
const selectionBackgroundColor: string = DEFAULT_OUTLOOK_THEME_COLOR;

// Background color when availability is Free
const freeBackgroundColor: string = '#48C471';

// Background color when availability is Busy
const busyBackgroundColor: string = '#EB5F63';

export interface SchedulingTemplateProps {
    event?: CalendarEvent;
    originalEvent?: CalendarEvent;
    model: SchedulingCalendarEvent;
    bookends?: GridBookendEvents;
}

/**
 * Template for showing attendee's free busy block on scheduling grid
 */
export const SchedulingFreeBusyItemTemplate = observer((props: SchedulingTemplateProps) => {
    const { event, model } = props;
    const { RequiredAttendees, OptionalAttendees, Resources } = event;
    if (!shouldShowFreeBusy(event) || !model.Start || !model.End) {
        return <></>;
    }

    const allAttendees = RequiredAttendees.concat(OptionalAttendees, Resources, {
        Mailbox: getCalendarOwnerFromItem(event),
    });
    const isShortItem =
        isFeatureEnabled('cal-mf-shortenEventBoxSurface') &&
        differenceInMinutes(model.End, model.Start) < SurfaceConstants.DefaultDurationInMinutes;

    return (
        <div
            className={classNames(
                styles.content,
                {
                    freeBusyBlock:
                        getConflictCount(
                            allAttendees,
                            model.Start,
                            getEndTimeToShowFreeBusy(model.Start, model.End)
                        ) > 0,
                },
                { shortItem: isShortItem }
            )}
        />
    );
});

/**
 * Template for showing selected time block on scheduling grid
 */
export const SchedulingSelectionItemTemplate = observer((props: SchedulingTemplateProps) => {
    const { event, originalEvent, bookends } = props;

    if (!event.Start || !event.End) {
        return null;
    }

    const isExp =
        isFeatureEnabled('cal-mf-timeSuggestions-exp-a') ||
        isFeatureEnabled('cal-mf-timeAccommodations');
    const start = event.Start;
    const end = getEndTimeToShowFreeBusy(event.Start, event.End);
    const organizer: EmailAddressWrapper = getCalendarOwnerFromItem(event);
    const organizerFreeBusy: FreeBusyType = freeBusyCache.getAggregatedFreeBusy(
        organizer.EmailAddress,
        start,
        end,
        event.IsAllDayEvent,
        originalEvent
    );

    const organizerConflict: boolean = isConflictFromFreeBusy(organizerFreeBusy);
    const allAttendees = event.RequiredAttendees.concat(event.OptionalAttendees);
    const allAttendeesExceptOrganizer = allAttendees.filter(
        attendee => attendee.Mailbox.EmailAddress != organizer.EmailAddress
    );
    const showAvailabilityAsFree = allAttendeesExceptOrganizer.length == 0 && !organizerConflict;
    const textStyle: any = { color: 'black' };
    let secondLine = '';
    const isNonDraftEventOriginalTime = !event.IsDraft && isOriginalTime(originalEvent, event);

    let backgroundColor = isNonDraftEventOriginalTime
        ? selectionBackgroundColor
        : organizerConflict
        ? busyBackgroundColor
        : showAvailabilityAsFree
        ? freeBackgroundColor
        : getUnknownAvailabilityBackgroundColor();

    if (shouldShowFreeBusy(event)) {
        const attendeeConflictCount: number = getConflictCount(
            allAttendeesExceptOrganizer,
            start,
            end,
            originalEvent
        );
        const locationConflictCount: number = getConflictCount(
            event.Resources,
            start,
            end,
            originalEvent
        );

        const hasConflict: boolean =
            organizerConflict || attendeeConflictCount > 0 || locationConflictCount > 0;

        backgroundColor = isNonDraftEventOriginalTime
            ? selectionBackgroundColor
            : hasConflict
            ? busyBackgroundColor
            : freeBackgroundColor;
        secondLine = getSchedulingItemSecondLineText(
            allAttendees,
            allAttendeesExceptOrganizer,
            event.Start,
            event.End,
            event.IsAllDayEvent,
            isNonDraftEventOriginalTime,
            organizerConflict,
            attendeeConflictCount,
            locationConflictCount,
            event,
            originalEvent
        );
    }

    const ariaLabel = getAriaLabel(event.Start, event.End, secondLine);

    const { beforeEvent, afterEvent } = bookends || {};
    const beforeEventHeight = beforeEvent ? getBookendHeight(beforeEvent.duration) : 0;
    const afterEventHeight = afterEvent ? getBookendHeight(afterEvent.duration) : 0;

    const hasAfterEvent = !!afterEvent;

    const shouldShortenEventBox = isFeatureEnabled('cal-mf-shortenEventBoxSurface');
    const eventDuration = differenceInMinutes(event.End, event.Start);
    const isShortItem =
        shouldShortenEventBox && eventDuration < SurfaceConstants.DefaultDurationInMinutes;
    const isShortestItem =
        shouldShortenEventBox &&
        eventDuration * TimeConstants.MillisecondsIn1Min < TimeConstants.MillisecondsIn20Mins;

    return (
        <div
            tabIndex={0}
            className={classNames(
                styles.selectedItem,
                { noBoxShadow: hasAfterEvent },
                { thickBorder: isExp },
                { seeThrough: isExp },
                { shortItem: isShortItem },
                { shortestItem: isShortestItem }
            )}
            style={{ color: backgroundColor }}
            aria-label={ariaLabel}
            aria-live="polite">
            <TooltipHost content={secondLine} overflowMode={TooltipOverflowMode.Parent}>
                {beforeEvent && (
                    <div
                        className={styles.bookends}
                        style={{
                            backgroundColor: backgroundColor,
                            height: `${beforeEventHeight}px`,
                            top: `-${beforeEventHeight + 1}px`,
                        }}>
                        {beforeEvent.subject}
                    </div>
                )}
                {!renderAsAllDay(event) && makeResizeEdge(styles.topResizable)}
                <span
                    className={classNames({ seeThrough: isExp, hidden: isExp })}
                    style={textStyle}>
                    <span
                        className={classNames(styles.selectedTime, {
                            smallerFont: isShortestItem,
                        })}>
                        {getDurationText(event)}
                    </span>
                    {differenceInMinutes(event.End, event.Start) >= 45 && <br />}
                    {secondLine}
                </span>
                {!renderAsAllDay(event) && makeResizeEdge(styles.bottomResizable)}
                {afterEvent && (
                    <div
                        className={classNames(styles.bookends, styles.afterEvent)}
                        style={{
                            backgroundColor: backgroundColor,
                            height: `${afterEventHeight}px`,
                            bottom: `-${afterEventHeight + 1}px`,
                        }}>
                        {afterEvent.subject}
                    </div>
                )}
            </TooltipHost>
        </div>
    );
});

function getUnknownAvailabilityBackgroundColor() {
    const rawPalette = getPaletteAsRawColors();
    return rawPalette.neutralTertiaryAlt;
}

function getBookendHeight(eventDuration: number): number {
    const duration =
        eventDuration > SurfaceConstants.DefaultDurationInMinutes
            ? eventDuration
            : SurfaceConstants.DefaultDurationInMinutes;
    const scaleFactor = SurfaceConstants.HourRowHeight / 60;
    return duration * scaleFactor;
}

function makeResizeEdge(className: string): JSX.Element {
    //don't render resize handle if on a touch only device since resize/drag funtionality is disabled in that case
    return getIsTouchOnlyDevice() ? null : (
        <span className={classNames(styles.resizableEdge, className)} />
    );
}

function getAriaLabel(start: OwaDate, end: OwaDate, conflictInfo: string) {
    return format(
        loc(schedulingAssistantAriaDateAndStatus),
        formatMeetingTimeSpan(start, end),
        conflictInfo
    );
}
